import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useState, useRef } from "react";

import downloadSvg from '../assets/download.svg';
import printSvg from '../assets/print.svg';
import mapSvg from '../assets/map.svg';
import layerSvg from '../assets/layer.svg';

import useFetchData from "../hooks/useFetchData";
import { categorizeData, getSvgMap, getIndexMetricData } from "../utils/dashboardUtils";
import { handleExcelDownload } from "../utils/excelExportModule";

import LegendMetricModal from "../components/LegendMetricModal";
import LayersModal from "../components/layers/LayersModal";
import MetricsModal from "./MetricsModal";
import MappableMetricsModal from "../components/mappableModal/MappableMetricsModal";

import MetricCategoriesContext from "../context/MetricCategoriesContext";
import LayersContext from "../context/LayersContext";
import { useMetadata } from "../context/MetadataContext";

import DownloadLoading from "../components/DownloadLoading";
import LoadingIndicator from "../components/LoadingIndicator";

import DemographicMetrics from "../components/panels/DemographicMetrics";
import AccessibilityMetrics from "../components/panels/AccessibilityMetrics";
import EnvironmentMetrics from "../components/panels/EnvironmentMetrics";
import EconomyMetrics from "../components/panels/EconomyMetrics";
import LivabilityMetrics from "../components/panels/LivabilityMetrics";
import LayerPanel from "../components/layers/LayerPanel";
import ClearMapSelectionModal from "../components/ClearMapSelectionModal";
import { DashboardColorContext } from "../context/DashboardColorContext";
import EducationMetrics from "../components/panels/EducationMetrics";
import { toggleOverlayLayer } from "../utils/messages";

const Dashboard = () => {
    const isIframe = window.self !== window.top;
    const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const { layers } = useContext(LayersContext);
    const { metricCategories } = useContext(MetricCategoriesContext);
    // Fetch metric metadata for the current org
    const metadata = useMetadata();

    const [currentTab, setCurrentTab] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [legendUrl, setLegendUrl] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [siteUrl, setSiteUrl] = useState('');
    const [legendId, setLegendId] = useState(0);

    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const [isMappableModalOpen, setIsMappableModalOpen] = useState(false);
    const [isLayersModalOpen, setIsLayersModalOpen] = useState(false);
    const [shouldDownloadExcel, setShouldDownloadExcel] = useState(false);
    const [showDownloadLoading, setShowDownloadLoading] = useState(false);

    // Geographic levels and exclusion lists defined for excel (will vary by project)
    const geographicLevels = [
        ...new Set(metadata?.flatMap((metric) => 
          metric.versions.map((version) => version.level_of_detail)
        ))
    ];

    const summaryExclusionList: string[] = [];
    const disaggregationExclusionList: string[] = [];
    const geographyExclusionList: string[] = [];

    const { indicators, loading, dashboardView, geoDetails, geoTag, selectionActive, lodMissing } = useFetchData(currentTab || '');

    const hardCodedCategories = ['Overview', 'Accessibility', 'Livability', 'Economy', 'Environment', 'Education'];
    const tabs = isIframe ? hardCodedCategories : metricCategories;

    console.log('tabs:', tabs)

    const currentIndexMetric = useMemo(() => {
        if (
            !currentTab ||
            !metadata?.length ||
            !indicators?.length
        ) {
            return null;
        }

        const {
            indexMetricId,
            indexMetricData
        } = getIndexMetricData(currentTab, metadata, indicators);

        if (
            indexMetricId == null ||
            !indexMetricData?.name
        ) {
            return null;
        }

        return {
            id: indexMetricId,
            name: indexMetricData.name
        };
    }, [currentTab, metadata, indicators]);

    const lastOverlayMessageRef = useRef<string | null>(null);

    useEffect(() => {
        if (
            loading ||
            !currentIndexMetric
        ) {
            return;
        }

        const messageKey = [
            currentTab,
            currentIndexMetric.id,
            currentIndexMetric.name
        ].join(":");

        // Avoid sending the same message again when data or geography refetches.
        if (lastOverlayMessageRef.current === messageKey) {
            return;
        }

        toggleOverlayLayer(
            currentIndexMetric.id,
            currentIndexMetric.name
        );

        lastOverlayMessageRef.current = messageKey;
    }, [
        currentTab,
        currentIndexMetric?.id,
        currentIndexMetric?.name,
        loading
    ]);

    useEffect(() => {
        if (!isIframe && tabs.length > 0) {
            if (tab && tabs.includes(tab)) {
                setCurrentTab(tab.toLowerCase());
            } else if (!currentTab) {
                setCurrentTab(tabs[0]);
            }
        }
    }, [tab, tabs]);

    useEffect(() => {
        if (isIframe && dashboardView && tabs.length > 0) {
            // Check if dashboardView matches a layer_name and add it to tabs
            const foundLayer = layers.find((layer) => layer.layer_name.toLowerCase() === dashboardView.toLowerCase());
    
            // If foundLayer is present, update the tabs
            if (foundLayer && !tabs.includes(dashboardView)) {
                const updatedTabs = [...tabs, dashboardView];
                setCurrentTab(updatedTabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || updatedTabs[0]);
            } else {
                setCurrentTab(tabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || tabs[0]);
            }
        } else {
            // When not in iframe, just ensure the currentTab is set based on the `tab` param
            setCurrentTab(tabs.find((t) => t.toLowerCase() === currentTab?.toLowerCase()) || tabs[0]);
        }
    }, [dashboardView, isIframe, tabs, layers]);

    // Combine data from all sources
    const allDataAvailable = useMemo(() => {
        return !loading && indicators;
    }, [loading, indicators]);


    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.message === 'pdf') {
                setPdfUrl(event.data.params.url);
                setLogoUrl(event.data.params.logoUrl || '');
                setLegendUrl(event.data.params.legendUrl || '');
                setSelectedArea(event.data.params.selectedArea || '');
                setSiteUrl(event.data.params.siteUrl || '');
                setIsModalOpen(true);
            
                // Send message to expand
                window.parent.postMessage({ 
                    message: "expand",
                    mode: "fullscreen"
                }, '*');
                console.log('expand message sent');
            } else if (event.data.message === 'metric-modal') {

                // Legend metadata info icon event
                console.log('Message received from parent app inside legend metric modal:', event.data);
                setLegendId(event.data.params.id);
            } else if (event.data.message === 'excel') {
                // Get selected area
                const sArea = event.data?.params?.selectedArea;
                // Show loading modal
                setShowDownloadLoading(true);
                
                if (!loading) {
                    // Execute excel download script
                    handleExcelDownload(geoDetails, geoTag, metricCategories, sArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                        setShowDownloadLoading(false);
                    });
                } else {
                    console.log('Data is still loading, will attempt to download Excel later.');
                    setShouldDownloadExcel(true); // Set flag to retry download
                }
            }
        };
    
        window.addEventListener('message', handleMessage);
    
        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [allDataAvailable, geoDetails]);

    useEffect(() => {
        if (!loading && shouldDownloadExcel) {
            setShowDownloadLoading(true);
            handleExcelDownload(geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShouldDownloadExcel(false); 
                setShowDownloadLoading(false);
            });
        }
    }, [loading, shouldDownloadExcel, geoDetails]);

    const closeLegendModal = () => {
        setIsLegendModalOpen(false);
        setLegendId(-1);
    };

    const closeModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        let commerceOrigin = '*';

        e.preventDefault();
        setIsModalOpen(false);
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, commerceOrigin);
        console.log('expand message sent');
    };

    const handleDownloadNoIframe = () => {
        // Show loading modal
        setShowDownloadLoading(true);
                
        if (!loading) {
            // Execute excel download script
            handleExcelDownload(geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShowDownloadLoading(false);
            });
        }
    };

    const handlePrintNoIframe = () => {
        setPdfUrl('');
        setLogoUrl('');
        setLegendUrl('');
        setSelectedArea('');
        setSiteUrl('');
        setIsModalOpen(true);
    };

    const handleShowLayers = () => {
        setIsLayersModalOpen((prevState) => !prevState);
    };

    const handleMappableModal = () => {
        setIsMappableModalOpen(true);
    }

    const closeMappableModal = () => {
        setIsMappableModalOpen(false);
    }

    const categorizedData = categorizeData(indicators);

    const renderMetrics = (tab: string) => {
        // Check if the tab exists in metricCategories
        if (!metricCategories.includes(tab)) {
            return <LayerPanel dashboardView={tab} />;
        }

        // Show modal to clear map selection if the current active selection is missing data or invalid (Small area problem)
        if (lodMissing) {
            return <ClearMapSelectionModal/>
        }
    
        // Show loading indicator if data is still being fetched
        if (loading) {
            return <LoadingIndicator />;
        }

        console.log('tab:', tab);
    
        // Map tab names to corresponding components
        const metricsComponents: Record<string, JSX.Element> = {
            Livability: <LivabilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Economy: <EconomyMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Environment: <EnvironmentMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Education: <EducationMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Accessibility: <AccessibilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Overview: <DemographicMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />
        };
    
        // Return the corresponding component or null if the tab is not found
        return (
            <div className="bg-[#f8f8fa]">
                <DashboardColorContext.Provider value={"#1A3A67"}>
                    {metricsComponents[tab] || null}
                </DashboardColorContext.Provider>
            </div>
        );
    };

    // Set active tab to current tab if present
    const activeTab = currentTab?.toLowerCase() || tabs[0];

    return (
        <div className={`${!isIframe ? 'max-w-[456px] mx-auto print:max-w-full' : ''}`}>
            <div className="flex mr-4 items-center justify-end ">
            </div>
            {!isIframe && (
                <div className="my-2 mx-5 flex items-center justify-end gap-3">
                    {layers.length > 0 && <img src={layerSvg} alt="layer icon" title="Show metric layers" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleShowLayers() }}/> }
                    <img src={printSvg} alt="print icon" title="Print PDF" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handlePrintNoIframe() }}/>
                    <img src={downloadSvg} alt="download icon" title="Download Excel" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleDownloadNoIframe() }}/>
                    <img src={mapSvg} alt="map icon" title="Show Mappable Metrics" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleMappableModal() }}/>
                </div>
            )}
           {!isIframe && <div className="flex m-4 items-center justify-center flex-wrap no-print">
                {tabs.map(tabName => (
                    <button
                        key={tabName}
                        className={`flex flex-col items-center justify-center w-32 h-8 p-1 rounded-md text-[10px] ${activeTab === tabName.toLowerCase() ? 'bg-color-cai-dashboard text-white' : 'bg-white text-color-cai-dashboard'}`}
                        onClick={() => {
                            navigate(`/dashboard/${tabName}`);
                            setCurrentTab(tabName);
                        }}
                    >
                        {tabName}
                    </button>
                ))}
            </div> }
            {/* Render metrics for current tab */}
            <div className="no-print">
                {!isModalOpen && allDataAvailable ? renderMetrics(currentTab) : <div className="loading-container">
                    <div className="loading-spinner"></div>
                </div>}
            </div>
            <LegendMetricModal
                id={legendId}
                isOpen={isLegendModalOpen}
                onClose={closeLegendModal}
                geoDetails={geoDetails}
                svg={getSvgMap(legendId)}
                geoTag={geoTag}
                selectionActive={selectionActive}
            />

            {isModalOpen && <MetricsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                geoDetails={geoDetails}
                geoTag={geoTag}
                metadata={metadata}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                legendUrl={legendUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
                selectionActive={selectionActive}
            />}

            {isMappableModalOpen && <MappableMetricsModal
                isOpen={isMappableModalOpen}
                onClose={closeMappableModal}
            />}

            {isLayersModalOpen && <LayersModal isOpen={isLayersModalOpen} onClose={handleShowLayers} />}

            {showDownloadLoading &&
                <DownloadLoading/>
            }
        </div>
    );
}

export default Dashboard;